/**
 * The Payments block of "Mein Zimmer" (COL-533).
 *
 * Every figure comes from `useTenantMoneyFacts`, shared with the overview and
 * the shell's journey gate, so the three can never disagree about what a
 * tenant owes. That hook also owns the predicate choices that matter here:
 * the rent paid-check honours an admin attestation, and an in-flight SEPA
 * debit is not arrears.
 *
 * Paying, switching method and uploading proof still live in V1; this block
 * links there rather than reimplementing a money-moving flow.
 */

import CardFeeDialog from "@/components/tenant/CardFeeDialog";
import TenantBankTransferDetails from "@/components/tenant/v2/TenantBankTransferDetails";
import { useRoomRentAccount } from "@/hooks/useRoomRentAccount";
import { resolveBankTransferDetails } from "@/lib/rent-bank-transfer";
import PaymentMethodPicker from "@/components/tenant/v2/PaymentMethodPicker";
import { isPaymentStateUnknown } from "@/lib/payment-method-choice";
import { resolveRentRail } from "@/lib/rent-collection-rail";

import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useEffectivePaymentMethods } from "@/hooks/useEffectivePaymentMethods";
import { SepaMandateForm } from "@/components/dashboard/SepaMandateForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SEPA_CREDITOR_ID, SEPA_CREDITOR_NAME } from "@/lib/sepa-constants";
import { buildSepaMandateLabels } from "@/lib/sepa-mandate-labels";
import { createCardCheckoutUrl, payRentNowViaSepa } from "@/lib/pay-rent-now";
import { getPaymentsLabels } from "@/lib/tenant-dashboard-copy";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTenantMoneyFacts } from "@/hooks/useTenantMoneyFacts";
import { useTenantPaymentMethod } from "@/hooks/useTenantPaymentMethod";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor, localeFor } from "@/lib/i18n/languages";
import { remainingRentCents } from "@/lib/rent";
import { accruedFeeCents } from "@/lib/rent-owed-with-fees";
import { isDepositPayment } from "@/lib/tenant-payment-scope";
import { getTenantPaymentsLabels } from "@/lib/tenant-payments-copy";
import { formatTenantDate } from "@/lib/date-format";

type Props = {
  /** Scopes the ledger — a tenant with successive tenancies has rows from all of them. */
  booking: any;
};

/**
 * The month a date-only charge belongs to, formatted for `locale`.
 *
 * NOT `new Date("2026-10-01").toLocaleDateString(...)`. That parses the
 * date-only form as midnight UTC and then formats it in the BROWSER's
 * timezone, so anywhere west of UTC it renders the previous month -- a tenant
 * in New York would see "Rent September 2026" on the October charge. The
 * calendar components are read straight out of the string and rebuilt as a
 * local date, which is the same thing `parseLocalDate` does for the key
 * unlock rule. (Codex, 14 Oct.)
 */
export function billedMonth(dueDate: string, locale: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dueDate).trim());
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(dueDate);
  return d.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

export default function TenantPaymentsSection({ booking }: Props) {
  const { tDict, language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const d = translateDictionary(
    "tenant.payments",
    getTenantPaymentsLabels(language),
    (key, literal) => tDict(key, literal ?? "", activeLanguage)
  );

  const locale = localeFor(activeLanguage);
  const eur = (cents: number) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
    }).format((cents ?? 0) / 100);
  const day = (iso?: string | null) => (iso ? formatTenantDate(iso) : "—");

  const money = useTenantMoneyFacts(booking);

  /*
    What the tenant currently pays by, for the design's method row.

    Read-only: `useTenantPaymentMethod` only SELECTs. The mandate itself is
    still granted through V1's SepaMandateForm, and "Change" links there —
    none of this adds a payment write path.
  */
  const {
    data: methodState,
    /*
      `isPending`, not `isLoading`. In TanStack v5 isLoading is
      (isPending && isFetching), so a query that has not fetched yet but is
      not currently in flight reports false -- and this value feeds
      "do we know whether a mandate exists". isPending is the one that means
      "no answer yet", which is the question being asked.
    */
    isPending: methodPending,
    isError: methodError,
  } = useTenantPaymentMethod(booking);
  const method = methodState ?? {
    preferred: null,
    last4: null,
    mandateActive: false,
    dueDay: null,
  };
  /*
    `mandateActive: false` is this hook's DEFAULT, not an answer.

    While the query is in flight, or after it fails, `method` falls back to
    the literal above -- so "no mandate" and "we do not know yet" are the
    same value. Everything downstream that branches on `mandateActive` has to
    know the difference, because both branches are wrong when the truth is
    unknown: the picker would let a tenant switch away from a live mandate
    (and keep being debited), and the summary would tell them to transfer
    rent that is already being collected.
  */
  const effective = useEffectivePaymentMethods(booking);
  /*
    Where a transfer should actually go, resolved PER ROOM through the same
    chain V1 uses (room_payment_config -> get_room_rent_account -> CoLiviq
    last resort). This card used to read `VITE_COLIVIQ_IBAN`, which is unset
    in dev, so tenants saw "Bank details on request" where V1 showed a real
    IBAN (Neel, 14 Oct). Hook called unconditionally, above the early
    returns, so hook order cannot change between renders.
  */
  const rentAccount = useRoomRentAccount(booking?.rooms?.id);
  /*
    Covers BOTH queries, same as the picker's `stateUnknown` below.

    This used to hardcode `railsLoading`/`railsFailed` to false, so a rails
    resolver that was still loading or had failed read as "no mandate state
    is unknown" here even though `effective.isLoading`/`isError` were already
    being OR'd in at every call site below as a belt-and-braces check. That
    redundancy masked the gap it was patching around: anything that reads
    `mandateUnknown` directly, without separately re-adding the rails check --
    the bank-transfer-details gate further down -- saw the gap. (Codex, 14 Oct)
  */
  const mandateUnknown = isPaymentStateUnknown({
    railsLoading: effective.isLoading,
    railsFailed: effective.isError,
    mandateLoading: methodPending,
    mandateFailed: methodError,
    mandateLoaded: !!methodState,
  });
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [mandateOpen, setMandateOpen] = useState(false);
  /*
    The Paid list starts CLOSED. Declared up here with the other hooks, not
    beside the disclosure it drives: this component returns early while the
    money facts load, so a useState below that point would change hook order
    between renders.
  */
  const [paidOpen, setPaidOpen] = useState(false);
  /*
    Net cents awaiting the card-fee disclosure, or null when it is closed.
    Declared here with the other hooks: this component returns early while
    the money facts load, so a useState below that point would change hook
    order between renders.
  */
  const [cardFeeNetCents, setCardFeeNetCents] = useState<number | null>(null);
  const [payingNow, setPayingNow] = useState(false);
  /*
    The card fee dialog serves both rent and the deposit; this says which one
    is in flight so `confirmCardCheckout` opens the right Stripe session.
  */
  const [cardFeeIsDeposit, setCardFeeIsDeposit] = useState(false);
  /*
    V1's own payments dictionary. The mandate form's 38 labels must read
    identically on both dashboards — it is the same legal document.
  */
  const sepaCopy = getPaymentsLabels(language);
  const methodSummary = (() => {
    /*
      Say nothing rather than the wrong thing. Every branch below reads
      `mandateActive`, which defaults to false while unknown -- so without
      this the panel tells a tenant on direct debit to transfer the rent
      themselves, and some of them will.
    */
    /*
      Unknown covers the RAILS too, not just the mandate.

      `resolveRentRail` falls through to "transfer" when sepaEnabled is false
      — and it is false while get_room_payment_methods is in flight, or after
      it fails, or while the operator check is unresolved (which now fails
      closed). So this panel told a tenant on direct debit to transfer the
      rent themselves during that window. Saying nothing is the only honest
      answer when we cannot name the rail.
    */
    if (mandateUnknown || effective.isLoading || effective.isError)
      return d.loading;
    const day = method.dueDay ? String(method.dueDay) : "1";
    // Masked, never the full IBAN: the tenant only needs to recognise it.
    const iban = method.last4 ? ` (•••• ${method.last4})` : "";

    // The rail rule lives in lib/rent-collection-rail, mirroring
    // rent-charge-tick. Only the wording is decided here.
    const rail = resolveRentRail({
      sepaEnabled: effective.sepaEnabled,
      cardEnabled: effective.cardEnabled,
      ownerCollects: effective.ownerCollects,
      externalOperatorBooking: effective.externalOperatorBooking,
      mandateActive: method.mandateActive,
      preferred: method.preferred,
    });

    if (rail === "sepa")
      return d.paymentMethodSepa.replace("{iban}", iban).replace("{day}", day);
    if (rail === "transfer")
      return d.paymentMethodTransfer.replace("{day}", day);
    if (rail === "card") return d.paymentMethodCard.replace("{day}", day);
    return d.paymentMethodNone;
  })();

  /*
    The same facts, split the way the design draws them: a NAME on its own
    line, the detail beneath it, and a badge when a mandate is live.

    Deliberately re-derived from `resolveRentRail` rather than parsed out of
    `methodSummary` -- that sentence is seeded copy in six languages, and
    splitting a translated string on a comma would break the moment one of
    them punctuates differently. `methodSummary` stays as the single-line
    fallback for the states where we must say nothing specific.
  */
  const methodDisplay = (() => {
    if (mandateUnknown || effective.isLoading || effective.isError) return null;
    const dueDay = method.dueDay ? String(method.dueDay) : "1";
    const iban = method.last4 ? `•••• ${method.last4} · ` : "";
    const rail = resolveRentRail({
      sepaEnabled: effective.sepaEnabled,
      cardEnabled: effective.cardEnabled,
      ownerCollects: effective.ownerCollects,
      externalOperatorBooking: effective.externalOperatorBooking,
      mandateActive: method.mandateActive,
      preferred: method.preferred,
    });
    if (rail === "sepa")
      return {
        name: d.railSepaName,
        detail: String(d.railSepaDetail ?? "")
          .replace("{iban}", iban)
          .replace("{day}", dueDay),
        active: !!method.mandateActive,
      };
    if (rail === "transfer")
      return {
        name: d.railTransferName,
        detail: String(d.railTransferDetail ?? "").replace("{day}", dueDay),
        active: false,
      };
    if (rail === "card")
      return {
        name: d.railCardName,
        detail: String(d.railCardDetail ?? ""),
        active: false,
      };
    return null;
  })();

  if (money.loading) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">{d.loading}</p>
      </Card>
    );
  }

  // A failed ledger fetch must never render as "nothing owed".
  if (money.error) {
    return (
      <Card className="p-6">
        <p className="text-destructive">{d.loadFailed}</p>
      </Card>
    );
  }

  const next = money.nextRent;
  const history = money.paidHistoryRows.slice(0, 6);

  /*
    Pay now is offered only when a rail actually exists for THIS booking.

    `create-card-checkout` does not enforce collection mode or the operator
    boundary itself, so falling through to Checkout on an owner-collected or
    external-operator booking would collect the tenant's rent into CoLiviq's
    Stripe account while they owe a different party.
  */
  const payRail =
    effective.sepaEnabled && method.mandateActive
      ? "sepa"
      : effective.cardEnabled
        ? "card"
        : null;

  /*
    The row must be LINKED to this booking.

    `rentRowsForBooking` deliberately keeps rows with a null `booking_id`
    (legacy rows written before the column was populated), but
    `create-rent-payment` looks rows up BY booking_id — so for an unlinked
    row it would insert and charge a second one, and receipts already
    recorded against the original would not count toward the remaining
    balance. That is a double charge, so this path stays closed for them and
    the V1 link remains.
  */
  const rowLinkedToBooking =
    next != null &&
    booking?.id != null &&
    Number((next as { booking_id?: unknown }).booking_id ?? NaN) ===
      Number(booking.id);

  /*
    Nothing is offered until the resolver AND the mandate query have
    answered. With `mandateActive` defaulting to false, an early render
    routes a tenant who HAS a mandate to card checkout — leaving a payable
    card session beside an automatic debit.
  */
  const payNowReady =
    !effective.isLoading &&
    // A FAILED resolver is not an answer either. Without this, a thrown
    // get_room_payment_methods / platform_operator_id call leaves every flag
    // at its default and Pay now is decided on no evidence at all.
    !effective.isError &&
    !methodPending &&
    !methodError &&
    !!payRail;

  /*
    Only a confirmed disclosure reaches Stripe. Cancelling -- including by
    Esc or backdrop -- leaves the rent row untouched; nothing is charged and
    nothing is renamed.
  */
  const confirmCardCheckout = async (proceed: boolean) => {
    const amountCents = cardFeeNetCents ?? 0;
    const isDeposit = cardFeeIsDeposit;
    setCardFeeNetCents(null);
    setCardFeeIsDeposit(false);
    if (!proceed) return;
    const bookingId = booking?.id != null ? Number(booking.id) : null;
    if (!bookingId || !(amountCents > 0)) return;
    setPayingNow(true);
    try {
      /*
        Deposit: no pre-created row and no `next`. `create-card-checkout`
        self-computes the expected deposit from the booking and ignores the
        client amount, and `confirm-payment-from-session` creates/recovers the
        row after Stripe (COL-291). The dueDate is metadata only (V1 uses the
        move-in/start date), so the server's recovery insert can date the row.

        ponytail: the fee dialog bills the card fee on `depositRemainingCents`
        while the server bills it on its self-computed expected deposit. These
        agree for a fully-unpaid deposit (the only case here — deposits are
        paid in one shot); revisit if part-paid deposits ever ship.
      */
      const url = isDeposit
        ? await createCardCheckoutUrl({
            bookingId,
            type: "deposit",
            amountCents,
            dueDate: String(
              booking?.move_in_date ?? booking?.start_date ?? ""
            ).slice(0, 10),
          })
        : !next
          ? null
          : await createCardCheckoutUrl({
              bookingId,
              amountCents,
              dueDate: String(next.due_date ?? "").slice(0, 10),
              paymentId: next.id,
            });
      if (!url) {
        toast.error(d.payNowFailed);
        return;
      }
      window.location.href = url;
    } catch (e: any) {
      toast.error(e?.message || d.payNowFailed);
    } finally {
      setPayingNow(false);
    }
  };

  /*
    Pay the deposit by card. Deposit-only tenants have no unpaid rent row, so
    the rent Pay now never rendered for them and the deposit showed with no way
    to settle it (the 13-Oct removal of the V1 hand-off deleted the only path).
    Opens the fee disclosure first, like the card rent path.
  */
  const payDeposit = () => {
    if (!(money.depositRemainingCents > 0)) return;
    setCardFeeIsDeposit(true);
    setCardFeeNetCents(money.depositRemainingCents);
  };

  const payNow = async () => {
    const bookingId = booking?.id != null ? Number(booking.id) : null;
    /*
      The two rails take DIFFERENT amounts, and getting this backwards
      moves real money.

      SEPA (`create-rent-payment`) trusts the client: it computes
      `chargeCents = submitted - received_cents` AND rewrites the row with
      `amount_cents = submitted`. So it must be sent the FULL bill. Sending
      the remainder subtracts receipts twice -- a 650 bill with 200 received
      would collect 250 instead of 450 AND permanently reduce the bill to
      450; with 400 received it returns `alreadyPaid` and collects nothing.

      Card (`create-card-checkout`) is server-authoritative: it derives the
      remainder from the row itself and says so -- "this function has no
      other gate on the amount, so it is the one place that has to get this
      right for every caller". The client value does not drive the charge.
      What the client DOES own there is the fee disclosure, which must show
      the tenant what they will actually be charged: the remainder.

      So: full bill to SEPA, remainder to the card dialog.
    */
    const billedCents = Number(next?.amount_cents ?? 0);
    const owedCents = remainingRentCents(next as any);
    if (!bookingId || !next || !(owedCents > 0) || !payNowReady) return;
    setPayingNow(true);
    try {
      /*
        No `description`. This pays a row that already exists, and
        create-rent-payment writes `description ?? mutable.description` --
        so passing the panel's heading renamed the tenant's own rent row to
        "Next payment" and lost which month it was for.
      */
      const shared = {
        bookingId,
        // The FULL bill — create-rent-payment subtracts receipts itself.
        amountCents: billedCents,
        dueDate: String(next.due_date ?? "").slice(0, 10),
      };
      if (payRail === "sepa") {
        const outcome = await payRentNowViaSepa(shared);
        toast[outcome === "collected" ? "success" : "info"](
          outcome === "collected"
            ? d.payNowCollected
            : outcome === "alreadyPaid"
              ? d.payNowAlreadyPaid
              : d.payNowInFlight
        );
        queryClient.invalidateQueries({ queryKey: ["payments"] });
        return;
      }
      /*
        Card goes through the fee disclosure first.

        The tenant is charged subtotal + processing fee, so the amount
        leaving their account is NOT the amount on the rent row. V1 discloses
        that before Stripe; V2 went straight to checkout, which is the one
        difference here that costs the tenant money without telling them.
        Neel confirmed the notice stays (10 Oct).
      */
      // The REMAINDER — this is what the tenant is actually charged,
      // and the fee is calculated on it.
      setCardFeeNetCents(owedCents);
    } catch (e: any) {
      toast.error(e?.message || d.payNowFailed);
    } finally {
      setPayingNow(false);
    }
  };

  /*
    Rent is not the only thing a tenant owes.

    This block used to say "nothing is due" whenever no unpaid RENT row
    existed — while the overview was telling the same tenant to pay their
    deposit and linking them straight here. An unpaid deposit and the
    `accumulated_fees_eur` pool are both real debts and both have to show.

    `accruedFeeCents` is the COL-472 helper: the fee pool is one per tenant and
    is never part of any month's `amount_cents`, which is exactly why showing
    rent alone understated what was owed on this screen before.

    WHAT THIS DELIBERATELY DOES NOT DO: project a due date for a month that was
    never billed. V1 does, and V2 did for three review rounds — each patch to
    that projection (a lease-end cutoff, a timezone normalisation, an overdue
    badge) produced the next round's defects, because the date arithmetic
    crosses a helper with inconsistent UTC/local semantics. What is left here
    is only what the ledger actually states. A tenant whose months were never
    billed is not told they owe nothing either: see `nothingOwed` below.
  */
  const depositOwed = !money.depositSettled;
  const feeCents = accruedFeeCents(booking?.accumulated_fees_eur);
  const nothingOwed = !next && !depositOwed && feeCents <= 0;

  return (
    <Card className="divide-y divide-border/60">
      <div className="flex flex-wrap items-start justify-between gap-3 p-6">
        <div className="min-w-0">
          {next ? (
            <>
              {/*
                The design titles this row with the CHARGE ("Miete Oktober
                2026"), not the generic "Next payment" label, and puts the
                amount on the right.

                Titled from the row's own due date, never from
                `payments.description` (COL-533 design QA, item 5). That column
                is written in English with an em dash by `rentDescription()`
                and by the `confirm_booking_on_deposit_paid()` trigger — "Rent
                — October 2026" — so preferring it put an English title with a
                stray dash in front of every tenant, in every language.

                Safe to ignore the column here: this row comes from
                `rentRowsForBooking`, which filters to `type = 'rent'`, so
                there is no non-rent row whose own label we could be
                discarding. The paid list below DOES carry deposits, and
                labels them by type.
              */}
              <p className="font-semibold text-foreground">
                {next.due_date
                  ? `${d.rentLabel} ${billedMonth(String(next.due_date), locale)}`
                  : d.rentLabel}
              </p>
              {/*
                How it will be settled, not just when.

                "per SEPA" is claimed ONLY when the rail really is SEPA with a
                live mandate. Saying it otherwise tells a tenant the money will
                be taken automatically when in fact nobody is collecting it,
                and they do not pay.

                `tenant.payments.dueOn` is seeded as the bare label "Due on" /
                "Fällig am" with no `{date}` placeholder, so it is rendered as
                label + value rather than interpolated.
              */}
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                <p className="text-sm text-muted-foreground">
                  {methodDisplay?.active
                    ? String(d.nextPaymentCollected ?? "").replace(
                        "{date}",
                        day(next.due_date)
                      )
                    : `${d.dueOn} ${day(next.due_date)}`}
                </p>
                {/*
                  Status badge sits beside the due date, not under the amount
                  (COL-597). Three states, not two: a SEPA debit already
                  submitted to the bank is neither overdue nor merely
                  "upcoming" — saying "upcoming" while the money is in
                  transit invites a second payment.
                */}
                <Badge
                  variant={
                    money.rentOverdue
                      ? "destructive"
                      : money.rentInFlight
                        ? "outline"
                        : "secondary"
                  }
                  className={
                    money.rentOverdue
                      ? "bg-red-800 hover:bg-red-800"
                      : undefined
                  }
                >
                  {money.rentOverdue
                    ? d.overdue
                    : money.rentInFlight
                      ? d.sepaInProgressStatus
                      : d.upcoming}
                </Badge>
              </div>
            </>
          ) : nothingOwed ? (
            /*
              "No open charges ON RECORD" — not "you owe nothing". A month that
              was never billed leaves no row behind, so the ledger cannot tell
              those two apart and this says only what it knows. The Manage
              payments link below goes to V1, which does project the next cycle.
            */
            <p className="font-semibold text-foreground">{d.nothingDue}</p>
          ) : null}

          {/*
            The deposit is no longer a line UNDER Next payment (Neel, 13 Oct):
            the stack of rent + deposit + fees read as three separate bills.
            Presentation only -- nothing is deleted, and a paid deposit does
            appear in the Paid list below. (It did not until the list stopped
            being rent-only; the claim predated the behaviour.)

            It survives in exactly one case: when the deposit is the ONLY
            thing outstanding. Dropping it there would leave this card blank
            -- `nothingOwed` counts the deposit, so it would not say "no open
            charges" either -- and a tenant would be given no amount at all
            for money they genuinely owe. So it is not a row beneath the rent;
            it IS the outstanding figure.
          */}
          {!next && depositOwed && money.depositRemainingCents > 0 ? (
            <div>
              <p className="text-sm text-muted-foreground">
                {d.securityDeposit}
              </p>
              <p className="text-3xl font-semibold tracking-tight text-foreground">
                {eur(money.depositRemainingCents)}
              </p>
            </div>
          ) : null}

          {feeCents > 0 ? (
            <div className="mt-3">
              <p className="text-sm text-muted-foreground">
                {d.dunningFeesLabel}
              </p>
              <p className="font-semibold text-foreground">{eur(feeCents)}</p>
            </div>
          ) : null}
        </div>
        {/*
          Amount alone on the RIGHT of the row, as the design draws it. The
          REMAINING balance, not the billed amount: a €650 row with €300
          already received owes €350.
        */}
        {next ? (
          <p className="shrink-0 text-lg font-semibold tracking-tight text-foreground">
            {eur(remainingRentCents(next))}
          </p>
        ) : null}
      </div>

      {/*
        The design's Payment method row: what is set up, its state, and a way
        to change it. READ-ONLY here — granting a mandate, switching method
        and paying all still run through V1's existing flows, per Ganesh's
        "reuse what exists… restyle only, no new payment logic". Nothing on
        this row writes.
      */}
      <div className="border-t border-border/60 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/*
            The design titles this block with a small "Payment method" label
            and the METHOD beneath it. `methodDisplay` is null exactly when we
            cannot name the rail honestly -- mandate or rails still unknown, or
            no method set -- and then the single-line summary stands in for
            the name, which already says the right thing in those states.
          */}
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{d.paymentMethod}</p>
            <p className="font-semibold text-foreground">
              {methodDisplay?.name ?? methodSummary}
            </p>
            {/*
              Bank transfer drops the sub-line (COL-597): its due day and
              account sit in the bank card right below. SEPA and card keep
              theirs -- the SEPA line names the mandate account and the card
              line is what tells a tenant nothing is debited automatically.
            */}
            {methodDisplay && method.preferred !== "bank_transfer" ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {methodDisplay.detail}
              </p>
            ) : null}
          </div>
          {/*
            The control that opens the method picker. Without it a tenant on
            the wrong rail has no self-service way off it.
          */}
          <div className="flex shrink-0 items-center gap-2">
            {/*
              Only a live mandate is badged. The grey "Open" pill is gone
              (COL-597): it said nothing a tenant could act on.
            */}
            {method.mandateActive ? (
              <span className="rounded-full bg-foreground px-3 py-1.5 text-sm font-semibold text-background">
                {d.paymentMethodActive}
              </span>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 rounded-full"
              aria-expanded={pickerOpen}
              onClick={() => setPickerOpen((v) => !v)}
            >
              {d.paymentMethodChange}
            </Button>
          </div>
        </div>

        {/*
        Where the mockup shows SEPA, a tenant who is NOT on direct debit needs
        somewhere to send the money (Neel, 13 Oct): "display CoLiviq bank
        details when no payment method is set or when the selected method is
        bank transfer".

        Keyed on the saved preference, not on which rails exist. A booking can
        have SEPA available while the tenant has deliberately chosen transfer,
        and that tenant still has to be told where to pay. An ACTIVE mandate
        is the one case this stays hidden for even with no preference saved --
        the money is already being collected, and bank details beside a live
        direct debit invite a second, manual payment.

        `mandateUnknown` gates it for the same reason. `mandateActive: false`
        is this hook's DEFAULT while the query is in flight or after it fails,
        so "no mandate" and "we do not know yet" are the same value -- and
        printing bank details on a maybe would tell a tenant to transfer rent
        that is already being debited.
      */}
        {!mandateUnknown &&
        !method.mandateActive &&
        /*
        ONLY when CoLiviq is the one collecting.

        `ownerCollects` and an external-operator booking both mean the rent
        goes to somebody else's account. Printing CoLiviq's IBAN there does
        not merely mislabel the recipient -- once the environment IBAN is
        configured it sends the tenant's rent to the wrong bank account, and
        the owner is still owed it. (Codex, 14 Oct.)
      */
        !effective.ownerCollects &&
        !effective.externalOperatorBooking &&
        /*
        ...and not while a debit is already on its way to the bank.

        A mandate cancellation approved AFTER the month's debit was submitted
        deactivates the method and can flip the preference to transfer without
        settling that payment. Without this the card would then invite a
        manual transfer for rent that is about to be taken anyway, and the
        tenant pays twice. `Pay Now` below is already gated on the same flag.
      */
        !money.rentInFlight &&
        (method.preferred === "bank_transfer" || !method.preferred) ? (
          <div className="mt-4">
            <TenantBankTransferDetails
              labels={d as unknown as Record<string, string>}
              /*
              `ownerCollects` is false by construction here -- the gate above
              excludes those rooms entirely -- but it is passed honestly
              rather than hardcoded, because the resolver is shared with V1
              where it is the difference between the owner's account and
              CoLiviq's. Widening this block to owner-collected rooms (V1
              does show them the owner's account) is a separate decision.

              While either read is in flight or has failed we pass null, so
              the card says "on request" rather than naming an account we are
              not yet sure of.
            */
              details={
                rentAccount.isPending || rentAccount.isError
                  ? null
                  : resolveBankTransferDetails({
                      paymentConfig: rentAccount.paymentConfig,
                      rentAccount: rentAccount.rentAccount,
                      ownerCollects: effective.ownerCollects,
                      bankTransferEnabled: true,
                    })
              }
            />
          </div>
        ) : null}

        {/*
          Pay now. With an active mandate this debits immediately through
          `create-rent-payment` rather than opening a card checkout — the
          tenant has already authorised us, and sending them to Stripe would
          charge a second instrument. Without one it opens Stripe Checkout.

          Sits at the bottom of the payment-method row rather than in a row of
          its own (Neel, 26 Oct). Placement only: which rail it uses is still
          decided by `payRail` above, not by the method shown in this row.

          The BILLED amount is sent, not the remaining balance:
          `create-rent-payment` subtracts `received_cents` itself (COL-417)
          and answers `alreadyPaid` when nothing is open. A client that
          pre-subtracted would be a second opinion on what a tenant owes.
        */}
        {next && !money.rentInFlight && rowLinkedToBooking && payNowReady ? (
          <div className="mt-4">
            <Button
              className="rounded-full"
              disabled={payingNow}
              onClick={() => void payNow()}
            >
              {d.payNow}
            </Button>
          </div>
        ) : null}
      </div>

      {/*
        The design's three-way picker, opened by "Change" rather than a trip
        back to V1. It writes the same column V1 writes and defers to the
        same shared guard, so a tenant still cannot switch away from an
        active mandate without our approval.
      */}
      {pickerOpen ? (
        <PaymentMethodPicker
          bookingId={booking?.id != null ? Number(booking.id) : null}
          current={
            (method.preferred as
              | "sepa_debit"
              | "card"
              | "bank_transfer"
              | null) ?? null
          }
          last4={method.last4}
          mandateActive={method.mandateActive}
          /*
            Which rails this booking actually has. Offering SEPA on an
            owner-collected or external-operator booking would let a tenant
            select a method we will never debit — and then stop transferring
            rent they still owe.
          */
          sepaAvailable={effective.sepaEnabled}
          cardAvailable={effective.cardEnabled}
          transferAvailable={
            effective.methods?.bank_transfer_enabled === true ||
            effective.ownerCollects
          }
          /* Never assume the 1st: the booking carries its own due day. */
          dueDay={method.dueDay}
          /*
            Unknown covers BOTH queries, and failure as well as loading.
            Previously only `effective.isLoading`: a failed rails resolver
            reads as "no rails", and a loading or failed mandate query reads
            as "no mandate" -- either of which lets the tenant save a switch
            away from a direct debit that is still live. They then pay by
            hand and get debited too.
          */
          stateUnknown={isPaymentStateUnknown({
            railsLoading: effective.isLoading,
            railsFailed: effective.isError,
            mandateLoading: methodPending,
            mandateFailed: methodError,
            mandateLoaded: !!methodState,
          })}
          /*
            Saved means done: the section folds back up so the tenant sees the
            method line they just changed, rather than the three cards they
            chose from (Neel, 23 Oct).
          */
          onSaved={() => setPickerOpen(false)}
          d={d as unknown as Record<string, string>}
        />
      ) : null}

      <div className="p-6 empty:hidden">
        {/*
          Granting a mandate happens HERE now, not on V1.

          This mounts the SAME `SepaMandateForm` V1 mounts — a §19-compliant
          mandate form — with the same creditor constants and the same 38
          labels, built by the shared `buildSepaMandateLabels`. Nothing about
          the form, or the mandate it writes, is reimplemented; only the
          container is V2's.

          Offered only when the resolver says SEPA is a rail for this
          booking: a tenant granting us a mandate we will never debit is
          worse than no button at all.
        */}
        {/*
          Pay the deposit by card. Shown only when the deposit is the sole
          outstanding item (no rent row), the contract is signed, and card is a
          resolved rail for this booking — the same `effective` gate the method
          picker uses, failing closed while rails are loading or errored so a
          button never appears on a booking we must not collect (COL-353).
        */}
        {!next &&
        money.depositActionable &&
        money.depositRemainingCents > 0 &&
        effective.cardEnabled &&
        !effective.isLoading &&
        !effective.isError ? (
          <Button
            className="rounded-full"
            disabled={payingNow}
            onClick={() => payDeposit()}
          >
            {sepaCopy.payDeposit}
          </Button>
        ) : null}
      </div>

      {/*
          No hand-off to V1 for the non-SEPA case.

          This used to fall back to a "Manage payments" link to
          `/dashboard?dashboard=v1&tab=payments` here — exactly the routing
          defect Neel reported (13 Oct): a control in the new dashboard that
          switches the tenant to the old payment area. It predates the
          "Change" button's in-dashboard three-way picker above, which
          already covers card/bank-transfer method switching without SEPA
          -- so once that landed, this branch had nothing left to hand off
          FOR. A property without SEPA simply has no mandate to grant or
          change, so there is nothing to render in this slot.
        */}
      {effective.sepaEnabled ? (
        <div className="border-t border-border/60 p-6">
          <p className="text-xs text-muted-foreground">{d.directDebit}</p>
          <Button
            variant="outline"
            className="mt-2 rounded-full"
            onClick={() => setMandateOpen(true)}
          >
            {method.mandateActive ? d.changeMandate : d.setUpSepa}
          </Button>
        </div>
      ) : null}

      {history.length > 0 ? (
        <div className="border-t border-border/60 p-6">
          {/*
            COLLAPSED until clicked (Neel, 13 Oct): "in the 'Bezahlt' or
            'Paid' section it should be a drop down that only opens when
            clicked, else the list will get long fast."

            This was briefly always-expanded for literal mockup fidelity
            (Vishnu, 14 Oct) and is now back to the disclosure Neel actually
            asked for, confirmed in review (Ganesh, 14 Oct): the mockup only
            ever illustrated the OPEN state.

            A real <button>, not a clickable div, so Enter and Space work and
            a screen reader announces the state — `aria-expanded` and
            `aria-controls` point at the list it opens. The count rides on
            the trigger so a tenant can see how much history there is
            without opening it.
          */}
          <button
            type="button"
            onClick={() => setPaidOpen((v) => !v)}
            aria-expanded={paidOpen}
            aria-controls="tenant-paid-history"
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <span className="min-w-0">
              <span className="block text-xs font-normal text-muted-foreground">
                {d.paymentHistory}
              </span>
              <span className="block font-semibold text-foreground">
                {String(d.paidToggle ?? "{n}").replace(
                  "{n}",
                  String(history.length)
                )}
              </span>
            </span>
            <span className="shrink-0 text-sm font-semibold text-muted-foreground underline underline-offset-4">
              {paidOpen ? d.paidHide : d.paidShow}
            </span>
          </button>
          <ul
            id="tenant-paid-history"
            hidden={!paidOpen}
            className="mt-3 space-y-2"
          >
            {history.map((p: any) => (
              <li
                key={String(p.id)}
                className="flex flex-wrap items-center justify-between gap-2"
              >
                <span className="w-24 shrink-0 text-sm text-muted-foreground">
                  {day(p.due_date)}
                </span>
                {/*
                  `payments.description` is the row's own label — the design
                  shows "Rent September 2026" / "Deposit" here, not a bare
                  date and amount. Falling back to the type keeps the column
                  from collapsing on an older row that has none.
                */}
                <span className="min-w-0 flex-1 truncate text-foreground">
                  {/*
                    Same rule as the title above — named from the due date,
                    never from the English em-dashed `description`.

                    Type-aware again now that the list carries deposits as
                    well as rent: labelling a settled deposit "Rent October"
                    would be worse than the dash ever was.
                  */}
                  {isDepositPayment(p)
                    ? d.deposit
                    : p.due_date
                      ? `${d.rentLabel} ${billedMonth(String(p.due_date), locale)}`
                      : d.rentLabel}
                </span>
                <span className="font-medium text-foreground">
                  {eur(p.amount_cents ?? 0)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <Dialog open={mandateOpen} onOpenChange={setMandateOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{sepaCopy.sepaFormTitle}</DialogTitle>
          </DialogHeader>
          {mandateOpen ? (
            <SepaMandateForm
              bookingId={booking?.id != null ? Number(booking.id) : null}
              creditorName={SEPA_CREDITOR_NAME}
              creditorId={SEPA_CREDITOR_ID}
              labels={buildSepaMandateLabels(sepaCopy)}
              onSuccess={() => {
                setMandateOpen(false);
                queryClient.invalidateQueries({
                  queryKey: ["payment-methods"],
                });
              }}
              onCancel={() => setMandateOpen(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/*
        Card fee disclosure. Mounted beside the mandate dialog so it is
        present on every render of this section, not only while Pay now is
        in flight.
      */}
      <CardFeeDialog
        netCents={cardFeeNetCents}
        locale={locale}
        labels={sepaCopy}
        onClose={(proceed) => void confirmCardCheckout(proceed)}
      />
    </Card>
  );
}
